import type { Dir } from '../wire';

const P1_KEYS: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  w: 'up',
  W: 'up',
  s: 'down',
  S: 'down',
  a: 'left',
  A: 'left',
  d: 'right',
  D: 'right',
};

const WASD_ONLY: Record<string, Dir> = {
  w: 'up',
  W: 'up',
  s: 'down',
  S: 'down',
  a: 'left',
  A: 'left',
  d: 'right',
  D: 'right',
};

const ARROWS_ONLY: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

const ALL_GAME_KEYS = new Set([
  ...Object.keys(P1_KEYS),
  'Escape',
  ' ',
  'Spacebar',
]);

export interface KeyboardOptions {
  /** 'single' -- arrows AND wasd both drive the local seat (SPEC.md §5
   * default). 'local2p' -- wasd drives seat 1, arrows drive seat 2,
   * simultaneously, for the optional same-device 2P mode. */
  mode: 'single' | 'local2p';
  onDir: (dir: Dir) => void;
  onDirP2?: (dir: Dir) => void;
  onPause: () => void;
  /** disabled while a text input (e.g. name/code field) has focus, or the
   * game isn't playing. */
  enabled: boolean;
}

/** True for elements whose OWN native keyboard activation we must not
 * cancel -- a focused <button> (the D-pad) activates on Space via the
 * browser's default keydown action; a <button>/<a> also activates on
 * Enter, but that path never gets preventDefault()'d below so it was never
 * broken. Only Space needs this check (see isActivationKey below). */
function isNativelyActivatable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'BUTTON' || tag === 'A' || tag === 'SUMMARY' || tag === 'INPUT') return true;
  const role = el.getAttribute?.('role');
  return role === 'button' || role === 'link';
}

/** Attaches window-level keydown/keyup listeners. Returns a cleanup fn.
 * Never lets an arrow/wasd/space keypress scroll the page while enabled
 * (preventDefault at the boundary, per SPEC.md §5) -- UNLESS the event
 * target is itself a natively-activatable control (e.g. a focused D-pad
 * <button>), in which case Space must be left alone so the browser's own
 * keydown->click activation still fires (root cause of BUGLEDGER.md #5:
 * this handler used to preventDefault() on Space globally, which cancels
 * that native activation for ANY focused button, not just the D-pad --
 * fixed generically here rather than special-casing the D-pad). Resolves
 * simultaneous/rapid presses to "last cardinal pressed" via a held-key
 * stack so releasing a newer key reverts control to an older key that is
 * still physically held. */
export function attachKeyboard(opts: KeyboardOptions): () => void {
  const heldP1: Dir[] = [];
  const heldP2: Dir[] = [];

  function currentTop(stack: Dir[]): Dir {
    return stack.length > 0 ? stack[stack.length - 1]! : 'none';
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (!opts.enabled) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    const activatable = isNativelyActivatable(target);

    if (e.key === 'Escape') {
      e.preventDefault();
      opts.onPause();
      return;
    }

    if (opts.mode === 'single') {
      const dir = P1_KEYS[e.key];
      if (dir) {
        // Arrow/WASD keys never trigger native button activation, so
        // preventDefault is always safe here (and still needed to stop
        // page scroll when focus is on the canvas/body).
        e.preventDefault();
        if (!heldP1.includes(dir)) heldP1.push(dir);
        opts.onDir(currentTop(heldP1));
      } else if (ALL_GAME_KEYS.has(e.key)) {
        // Space (and Spacebar) land here since they're not in P1_KEYS.
        // Space IS a native activation key for a focused button/link --
        // only suppress its default (page-scroll) behavior when focus is
        // NOT on such a control, so a focused D-pad button's own click
        // still fires.
        if (!activatable) e.preventDefault();
      }
      return;
    }

    // local2p
    const p1dir = WASD_ONLY[e.key];
    if (p1dir) {
      e.preventDefault();
      if (!heldP1.includes(p1dir)) heldP1.push(p1dir);
      opts.onDir(currentTop(heldP1));
      return;
    }
    const p2dir = ARROWS_ONLY[e.key];
    if (p2dir) {
      e.preventDefault();
      if (!heldP2.includes(p2dir)) heldP2.push(p2dir);
      opts.onDirP2?.(currentTop(heldP2));
      return;
    }
  }

  function onKeyUp(e: KeyboardEvent): void {
    if (!opts.enabled) return;
    if (opts.mode === 'single') {
      const dir = P1_KEYS[e.key];
      if (dir) {
        e.preventDefault();
        const idx = heldP1.indexOf(dir);
        if (idx >= 0) heldP1.splice(idx, 1);
        // No message needed to "stop" (the engine keeps a Pac moving in its
        // last dir until a wall or a new turn, matching classic Pac-Man --
        // see packages/engine/src/pacman.ts updatePac). If another game key
        // is still held, restore its direction.
        if (heldP1.length > 0) opts.onDir(currentTop(heldP1));
      }
      return;
    }
    const p1dir = WASD_ONLY[e.key];
    if (p1dir) {
      e.preventDefault();
      const idx = heldP1.indexOf(p1dir);
      if (idx >= 0) heldP1.splice(idx, 1);
      if (heldP1.length > 0) opts.onDir(currentTop(heldP1));
      return;
    }
    const p2dir = ARROWS_ONLY[e.key];
    if (p2dir) {
      e.preventDefault();
      const idx = heldP2.indexOf(p2dir);
      if (idx >= 0) heldP2.splice(idx, 1);
      if (heldP2.length > 0) opts.onDirP2?.(currentTop(heldP2));
    }
  }

  window.addEventListener('keydown', onKeyDown, { passive: false });
  window.addEventListener('keyup', onKeyUp, { passive: false });
  return () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };
}
